import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getProducts, updateProduct } from "../lib/api.ts";
import type { Product } from "../lib/api.ts";
import { SearchIcon } from "../components/Icon.tsx";
import { ProductFormModal } from "../components/ProductFormModal.tsx";
import { useIsMobile } from "../lib/use-is-mobile.ts";

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const fmt = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" });

function formatMoney(cents: number) {
  return fmt.format(cents / 100);
}

function typeLabel(t: string) {
  if (t === "service_addon") return "Service add-on";
  if (t === "part") return "Part";
  return "Retail";
}

export default function Products() {
  const { slug } = useParams<{ slug: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    getProducts(slug, { search: debouncedSearch || undefined })
      .then((data) => setProducts(data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  async function handleToggleActive(product: Product) {
    if (!slug || toggling) return;
    setToggling(product.id);
    try {
      await updateProduct(slug, product.id, { active: !product.active });
      load();
    } catch (err) {
      setError((err as Error).message ?? "Could not update product.");
    } finally {
      setToggling(null);
    }
  }

  const isMobile = useIsMobile();

  return (
    <div>
      <section className="zd-hero" style={{ marginBottom: 32 }}>
        <div className="zd-hero-text">
          <div className="zd-hero-kicker">
            Catalog <span className="dot">·</span> {products.length} {products.length === 1 ? "product" : "products"}
          </div>
          <h1 className="zd-hero-headline">Products</h1>
          <p className="zd-hero-lead">Retail items, service add-ons, and parts you can add to a bill at checkout.</p>
        </div>
        <div className="zd-hero-actions">
          <div style={{ position: "relative", flex: "1 1 auto" }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-faint)", pointerEvents: "none" }}>
              <SearchIcon size={16} />
            </span>
            <input
              type="search"
              placeholder="Search products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input zd-page-search"
              style={{ paddingLeft: 36 }}
            />
          </div>
          <button
            type="button"
            className="btn-primary"
            style={{ whiteSpace: "nowrap" }}
            onClick={() => setEditing("new")}
          >
            Add product
          </button>
        </div>
      </section>

      {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ink-faint)", fontSize: 14 }}>
          Loading…
        </div>
      ) : products.length === 0 ? (
        <div className="zd-empty">
          <h3>{search ? "No matches." : "No products yet."}</h3>
          <p>
            {search
              ? "Try a different search term."
              : "Add retail items, service add-ons, or parts to include them on bills at checkout."}
          </p>
        </div>
      ) : isMobile ? (
        <div className="zd-card-list">
          {products.map((p) => (
            <div key={p.id} className="zd-card">
              <div className="zd-card-row">
                <span style={{ fontWeight: 600, fontSize: 15, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", fontFeatureSettings: "'tnum'" }}>{formatMoney(p.price_cents)}</span>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 12.5, color: "var(--ink-soft)" }}>
                <span className={p.active ? "badge-green" : "badge-red"}>{p.active ? "Active" : "Inactive"}</span>
                <span>{typeLabel(p.type)}</span>
                {p.taxable && <span>Taxable</span>}
              </div>
              <div className="zd-card-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditing(p)}>Edit</button>
                <button type="button" className="btn-secondary" disabled={toggling === p.id} onClick={() => void handleToggleActive(p)}>
                  {toggling === p.id ? "…" : p.active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            background: "var(--paper-raised)",
            borderRadius: 14,
            boxShadow: "var(--shadow-sm)",
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--paper-well)" }}>
                {[
                  { label: "Name", hide: "" },
                  { label: "Price", hide: "" },
                  { label: "Type", hide: "sm" },
                  { label: "Taxable", hide: "md" },
                  { label: "Active", hide: "" },
                  { label: "Actions", hide: "" },
                ].map((h, i) => (
                  <th
                    key={i}
                    className={
                      h.hide === "sm"
                        ? "hidden sm:table-cell"
                        : h.hide === "md"
                        ? "hidden md:table-cell"
                        : ""
                    }
                    style={{
                      padding: "14px 18px",
                      textAlign: "left",
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      fontSize: 10.5,
                      fontWeight: 500,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "var(--ink-faint)",
                    }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} style={{ borderTop: "1px solid var(--rule)" }}>
                  <td style={{ padding: "14px 18px", fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>
                    {p.name}
                  </td>
                  <td style={{ padding: "14px 18px", fontSize: 13, color: "var(--ink-soft)", fontFeatureSettings: "'tnum'" }}>
                    {formatMoney(p.price_cents)}
                  </td>
                  <td className="hidden sm:table-cell" style={{ padding: "14px 18px", fontSize: 13, color: "var(--ink-soft)" }}>
                    {typeLabel(p.type)}
                  </td>
                  <td className="hidden md:table-cell" style={{ padding: "14px 18px", fontSize: 13, color: "var(--ink-soft)" }}>
                    {p.taxable ? "Yes" : "No"}
                  </td>
                  <td style={{ padding: "14px 18px" }}>
                    <span
                      className={p.active ? "badge-green" : "badge-red"}
                    >
                      {p.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ padding: "14px 18px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ fontSize: 12.5, padding: "5px 12px" }}
                        onClick={() => setEditing(p)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ fontSize: 12.5, padding: "5px 12px" }}
                        disabled={toggling === p.id}
                        onClick={() => void handleToggleActive(p)}
                      >
                        {toggling === p.id
                          ? "…"
                          : p.active
                          ? "Deactivate"
                          : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && slug && (
        <ProductFormModal
          slug={slug}
          initial={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
